'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Target03Icon } from '@terra/ui-icons';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import { useMentions } from '../../../hooks/useMentions';
import { useChatStore } from '../../../store/chatStore';

const SIGNAL_FALLBACK_ID_LENGTH = 4;

const toSignalMentionId = (signalId: string) => `signal-${signalId}`;

export function useSignalCopilotAttack() {
  const tSignals = useTranslations('signals');
  const { addMention, selectedMentions } = useMentions();
  const appendTextToInput = useChatStore((state) => state.appendTextToInput);
  const attackWithCopilot = useCallback(
    (signals: readonly GroupedSignal[]): number => {
      const existingMentionIds = new Set(
        selectedMentions.map((mention) => mention.id),
      );

      signals.forEach((signal) => {
        const label =
          signal.description ||
          tSignals('signalFallbackLabel', {
            signalId: signal.id.slice(-SIGNAL_FALLBACK_ID_LENGTH),
          });

        addMention({
          id: toSignalMentionId(signal.id),
          label,
          icon: <Target03Icon size={12} />,
          type: 'child',
          mentionEntity: {
            entityType: 'Signal',
            entityId: signal.id,
            label,
            extraData: { signal },
          },
        });
      });
      setTimeout(() => {
        appendTextToInput(
          tSignals('bulkActions.copilotPrompt', { count: signals.length }),
        );
      }, 0);

      return signals.filter(
        (signal) => !existingMentionIds.has(toSignalMentionId(signal.id)),
      ).length;
    },
    [addMention, appendTextToInput, selectedMentions, tSignals],
  );

  return { attackWithCopilot };
}
